import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarDays } from "lucide-react";
import {
  useEmployeeAbsences,
  useEmployeeEntitlements,
} from "@/hooks/useEmployeeAbsences";
import { useEmploymentPeriods } from "@/hooks/useEmploymentPeriods";
import { usePublicHolidays } from "@/hooks/usePublicHolidays";
import { countVacationDays, countBusinessDays } from "@/lib/date-utils";

interface EntitlementCardProps {
  personId: string;
  year: number;
}

/** Weighted average employment percentage for a given year. */
export function computeYearlyEmploymentPct(
  periods: {
    percentage: number;
    valid_from: string;
    valid_to: string | null;
  }[],
  year: number,
): number {
  let totalDays = 0;
  let weightedPct = 0;

  for (let m = 1; m <= 12; m++) {
    const daysInMonth = new Date(year, m, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dow = new Date(year, m - 1, d).getDay();
      if (dow === 0 || dow === 6) continue;
      const dateStr = `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

      let pct = 0;
      for (const p of periods) {
        const from = p.valid_from;
        const to = p.valid_to;
        if (dateStr >= from && (!to || dateStr <= to)) {
          pct = p.percentage;
        }
      }
      totalDays++;
      weightedPct += pct;
    }
  }

  return totalDays > 0 ? weightedPct / totalDays : 0;
}

export function EntitlementCard({ personId, year }: EntitlementCardProps) {
  const { data: absences = [] } = useEmployeeAbsences(personId, year);
  const { data: entitlements = [] } = useEmployeeEntitlements(personId);
  const { data: periods = [] } = useEmploymentPeriods(personId);
  const { data: publicHolidays = [] } = usePublicHolidays(year);

  const publicHolidayDates = new Set(publicHolidays.map((h) => h.date));

  const entitlement = entitlements.find((e) => e.year === year);
  const baseVacationDays = entitlement?.vacation_days ?? 25;
  const carryOverDays = entitlement?.carry_over_days ?? 0;

  const employmentPct = computeYearlyEmploymentPct(periods, year);
  const vacationDays =
    Math.round(((baseVacationDays * employmentPct) / 100) * 10) / 10;
  const totalEntitled = vacationDays + carryOverDays;

  const usedDays = absences
    .filter((h) => h.absence_type === "vacation")
    .reduce(
      (sum, h) =>
        sum +
        countVacationDays(
          h.start_date,
          h.end_date,
          publicHolidayDates,
          employmentPct,
        ),
      0,
    );
  const remaining = Math.round((totalEntitled - usedDays) * 10) / 10;

  const sickDays = absences
    .filter((h) => h.absence_type === "sick")
    .reduce((sum, h) => sum + countBusinessDays(h.start_date, h.end_date), 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarDays className="h-4 w-4" />
          Vacation Balance {year}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xl font-bold">{remaining}</p>
            <p className="text-xs text-muted-foreground">Days remaining</p>
          </div>
          <div className="text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Entitled</span>
              <span>{vacationDays}</span>
            </div>
            {employmentPct < 100 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Employment</span>
                <span>{Math.round(employmentPct)}%</span>
              </div>
            )}
            {carryOverDays !== 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Carry-over</span>
                <span>
                  {carryOverDays > 0 ? "+" : ""}
                  {carryOverDays}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Used</span>
              <span>-{usedDays}</span>
            </div>
            {sickDays > 0 && (
              <div className="flex justify-between border-t pt-1 mt-1">
                <span className="text-muted-foreground">Sick days</span>
                <span>{sickDays}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
